import { Page } from '@components/page';
import { Seo } from '@components/Seo';
import SmallContainer from '@components/ui/layout/SmallContainer';
import { Heading } from '@components/ui/typography';
import { BlogCard } from '@components/_blog/Card';
import { BlogFeatured } from '@components/_blog/Featured';
import { BlogGrid } from '@components/_blog/Grid';
import { BlogPagination } from '@components/_blog/Pagination';
import { getDrafts, getSanityClient } from '@lib/sanity/client';
import { useIsMounted } from '@lib/use-is-mounted';
import { GetStaticPaths, GetStaticProps } from 'next';
import React, { FC } from 'react';
import { BlogItem, BlogSingleton } from '__generated__/sanity';
type BlogIndexPageProps = {
  content: BlogSingleton;
  featured: BlogItem;
  items: BlogItem[];
  total: number;
  current: number;
};
const BLOG_ITEMS_PAGE_LIMIT = 6;
const BlogIndexPage: FC<BlogIndexPageProps> = ({
  content,
  featured,
  items,
  current,
  total
}) => {
  const isMounted = useIsMounted();
  return <Page>
      <Seo data={content.fields.seo} />
      <SmallContainer className={fadeIn} data-visible={isMounted ? '' : undefined}>
        <Heading as="h1" variant="h1" mt={{
        _: '64',
        sm: '104'
      }} mb={{
        _: '40',
        sm: '80'
      }}>
          {content.fields.intro.title}
        </Heading>
        <BlogFeatured {...featured.fields} />
        <BlogGrid>
          {items.map(item => <BlogCard key={item._id} {...item.fields} />)}
        </BlogGrid>
        <BlogPagination current={current} total={total} perPage={BLOG_ITEMS_PAGE_LIMIT} />
      </SmallContainer>
    </Page>;
};
const fadeIn = "f1p15zkt";
export const getStaticProps: GetStaticProps<BlogIndexPageProps, {
  index: string;
}> = async ({
  preview = false,
  params
}) => {
  if (!params || !params.index) {
    return {
      notFound: true
    };
  }
  const current = parseInt(params.index, 10) - 1;
  const sanity = getSanityClient(true);
  const content = await sanity.fetch<BlogSingleton[]>(`* [_type == "blog-singleton"]`);
  // Get the first item that is featured
  let featured = await sanity.fetch<BlogItem[]>(`* [_type == "blogItem" && fields.summary.featured == true ] | order(fields.summary.date desc)`);
  if (!featured.length) {
    // If there's no featured item, get the most recent
    featured = await sanity.fetch<BlogItem[]>(`* [_type == "blogItem"] | order(fields.summary.date desc)`);
  }
  const {
    items,
    total
  } = await getBlogItems(current, preview, featured[0]._id);
  return {
    props: {
      content: getDrafts(content, preview)[0],
      featured: getDrafts(featured, preview)[0],
      items: getDrafts(items, preview),
      total: total,
      current
    }
  };
};
export const getStaticPaths: GetStaticPaths<{
  index: string;
}> = async () => {
  const {
    total
  } = await getBlogItems(0, false, '');
  // - 1 for the featured post
  const pages = Math.ceil(total / BLOG_ITEMS_PAGE_LIMIT);
  let paths = [];
  for (let i = 0; i < pages; i++) {
    paths.push(`/blog/page/${i + 1}`);
  }
  return {
    paths,
    fallback: false
  };
};
async function getBlogItems(page = 0, preview = false, excludeId: string): Promise<{
  total: number;
  items: BlogItem[];
}> {
  const sanity = getSanityClient(preview);
  const start = page * BLOG_ITEMS_PAGE_LIMIT;
  const end = start + BLOG_ITEMS_PAGE_LIMIT;
  const items = await sanity.fetch(`* [_type=="blogItem" && _id != "${excludeId}"] | order(fields.summary.date desc)  [${start}...${end}]`);
  // - 1 for the featured post
  const total = (await sanity.fetch(`count(*[_type=="blogItem"])`)) - 1;
  return {
    items,
    total
  };
}
export default BlogIndexPage;

require("./[index].linaria.module.css!=!../../../node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js?cacheProvider=!./[index].tsx");